import React, { useState } from 'react';
import { Tabs, Select, message, Modal, Form, InputNumber, Empty } from 'antd';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { withPrivateRoute } from '@/components/hoc/withPrivateRoute';
import RootLayout from '@/components/layouts/RootLayout';
import { ContainerFluid, Section } from '@/components/layouts';
import { ContentBackground } from '@/components/content';
import { ContentWrapper, ContentHeader, ContentBody } from '@/components/content';
import { ROLES_PERMISSIONS, ROLES } from '@/constants';
import { useAuthStore } from '@/store/useAuthStore';
import { API } from '@/services/api.service';
import AutomationDashboard from './_components/AutomationDashboard';
import AutomationConfigPanel from './_components/AutomationConfigPanel';
import AutomationLogsTable from './_components/AutomationLogsTable';
import WorkflowExecutionView from './_components/WorkflowExecutionView';
import ABSoftSyncPanel from './_components/ABSoftSyncPanel';
import AutomationCompaniesPanel from './_components/AutomationCompaniesPanel';
import PayrollSubmissionsPanel from './_components/PayrollSubmissionsPanel';
import AgentConfigPanel from './_components/AgentConfigPanel';
import dayjs from 'dayjs';

const { Option } = Select;

const QUERY_KEYS = {
  CONFIG: 'payroll-automation-config',
  LOGS: 'payroll-automation-logs',
  WORKFLOWS: 'payroll-automation-workflows',
  ABSOFT_HISTORY: 'payroll-automation-absoft-history',
  DASHBOARD_STATS: 'payroll-automation-dashboard-stats',
};

function PayrollAutomationPage() {
  const { t } = useTranslation(['tables', 'menus', 'forms', 'messages']);
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const isSuperAdmin = user?.roles?.includes(ROLES.superAdmin);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [activeTab, setActiveTab] = useState('submissions');
  const [workflowModalVisible, setWorkflowModalVisible] = useState(false);
  const [workflowForm] = Form.useForm();

  const pa = (key) => t(`payrollAutomation.${key}`, { ns: 'tables' });

  // Fetch automation companies (for company selector)
  const { data: companiesData } = useQuery({
    queryKey: ['automation-companies-dropdown'],
    queryFn: async () => await API.payrollAutomation.getAutomationCompaniesDropdown(),
  });

  const companies = companiesData?.data || [];

  // Fetch automation config
  const { data: configData, isLoading: configLoading } = useQuery({
    queryKey: [QUERY_KEYS.CONFIG, selectedCompany],
    queryFn: async () => await API.payrollAutomation.getAutomationConfig(selectedCompany),
    enabled: !!selectedCompany,
  });

  const automationConfig = configData?.data || [];

  // Fetch submissions (for dashboard stats)
  const { data: submissionsData, isLoading: submissionsLoading } = useQuery({
    queryKey: ['payroll-submissions-stats', selectedCompany],
    queryFn: () => API.payrollAutomation.getSubmissions({ companyId: selectedCompany, limit: 100 }),
    enabled: !!selectedCompany && (activeTab === 'dashboard' || activeTab === 'submissions'),
  });

  const submissions = submissionsData?.data || [];

  // Fetch automation logs
  const { data: logsData, isLoading: logsLoading } = useQuery({
    queryKey: [QUERY_KEYS.LOGS, selectedCompany],
    queryFn: async () => await API.payrollAutomation.getAutomationLogs({
      company: selectedCompany,
      page: 1,
      limit: 20,
    }),
    enabled: !!selectedCompany && activeTab === 'logs',
  });

  const logs = logsData?.data || [];
  const logsPagination = logsData?.pagination || {};

  // Fetch workflows
  const { data: workflowsData, isLoading: workflowsLoading } = useQuery({
    queryKey: [QUERY_KEYS.WORKFLOWS, selectedCompany],
    queryFn: async () => await API.payrollAutomation.getWorkflows({
      company: selectedCompany,
      page: 1,
      limit: 20,
    }),
    enabled: !!selectedCompany && activeTab === 'workflows',
  });

  const workflows = workflowsData?.data || [];

  // Fetch AB Soft sync history
  const { data: abSoftData, isLoading: abSoftLoading } = useQuery({
    queryKey: [QUERY_KEYS.ABSOFT_HISTORY, selectedCompany],
    queryFn: async () => await API.payrollAutomation.getAbSoftSyncHistory({
      companyId: selectedCompany,
      page: 1,
      limit: 20,
    }),
    enabled: !!selectedCompany && activeTab === 'absoft',
  });

  const abSoftHistory = abSoftData?.data || [];

  // Dashboard stats - based on submissions data
  const totalEmployees = submissions.reduce((sum, s) => sum + (s.employeeCount || s.employees?.length || 0), 0);
  const dashboardStats = {
    totalExecutions: submissions.length || 0,
    successfulExecutions: submissions.filter(s => s.status === 'completed').length || 0,
    failedExecutions: submissions.filter(s => s.status === 'cancelled').length || 0,
    runningExecutions: submissions.filter(s => ['draft', 'reviewed', 'approved', 'sent_to_absoft'].includes(s.status)).length || 0,
    activeWorkflows: totalEmployees,
    pendingActions: submissions.filter(s => s.status === 'draft').length || 0,
    activeRules: automationConfig.filter(c => c.enabled),
    recentActivity: submissions.slice(0, 5).map(sub => ({
      ruleType: `${sub.month}/${sub.year}`,
      status: sub.status === 'completed' ? 'SUCCESS' : sub.status === 'cancelled' ? 'FAILED' : 'RUNNING',
      timestamp: dayjs(sub.createdAt).format('DD.MM.YYYY HH:mm'),
    })),
  };

  // Mutations
  const createConfigMutation = useMutation({
    mutationFn: (data) => API.payrollAutomation.createAutomationConfig(data),
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.CONFIG, selectedCompany]);
      message.success(t('messages.createSuccess', { ns: 'messages' }));
    },
    onError: (error) => {
      message.error(error.message);
    },
  });

  const updateConfigMutation = useMutation({
    mutationFn: ({ configId, data }) => API.payrollAutomation.updateAutomationConfig(configId, data),
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.CONFIG, selectedCompany]);
      message.success(t('messages.updateSuccess', { ns: 'messages' }));
    },
    onError: (error) => {
      message.error(error.message);
    },
  });

  const deleteConfigMutation = useMutation({
    mutationFn: (configId) => API.payrollAutomation.deleteAutomationConfig(configId),
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.CONFIG, selectedCompany]);
      message.success(t('messages.deleteSuccess', { ns: 'messages' }));
    },
    onError: (error) => {
      message.error(error.message);
    },
  });

  const toggleAutomationMutation = useMutation({
    mutationFn: ({ configId, enabled }) => API.payrollAutomation.toggleAutomation(configId, enabled),
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.CONFIG, selectedCompany]);
    },
    onError: (error) => {
      message.error(error.message);
    },
  });

  const executeAutomationMutation = useMutation({
    mutationFn: ({ ruleType, data }) => API.payrollAutomation.executeAutomation(ruleType, data),
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.LOGS, selectedCompany]);
      message.success(t('operationSuccess', { ns: 'messages' }));
    },
    onError: (error) => {
      message.error(error.message);
    },
  });

  const retryAutomationMutation = useMutation({
    mutationFn: (logId) => API.payrollAutomation.retryAutomation(logId),
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.LOGS, selectedCompany]);
      message.success(t('operationSuccess', { ns: 'messages' }));
    },
    onError: (error) => {
      message.error(error.message);
    },
  });

  const runWorkflowMutation = useMutation({
    mutationFn: (data) => API.payrollAutomation.runMonthlyPayrollWorkflow(data),
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.WORKFLOWS, selectedCompany]);
      setWorkflowModalVisible(false);
      message.success(t('operationSuccess', { ns: 'messages' }));
    },
    onError: (error) => {
      message.error(error.message);
    },
  });

  const cancelWorkflowMutation = useMutation({
    mutationFn: (executionId) => API.payrollAutomation.cancelWorkflow(executionId),
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.WORKFLOWS, selectedCompany]);
      message.success(t('operationSuccess', { ns: 'messages' }));
    },
    onError: (error) => {
      message.error(error.message);
    },
  });

  const deleteWorkflowMutation = useMutation({
    mutationFn: (executionId) => API.payrollAutomation.deleteWorkflow(executionId),
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.WORKFLOWS, selectedCompany]);
      message.success(t('operationSuccess', { ns: 'messages' }));
    },
    onError: (error) => {
      message.error(error.message);
    },
  });

  const syncToAbSoftMutation = useMutation({
    mutationFn: (data) => API.payrollAutomation.syncToAbSoft(data),
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.ABSOFT_HISTORY, selectedCompany]);
      message.success(t('operationSuccess', { ns: 'messages' }));
    },
    onError: (error) => {
      message.error(error.message);
    },
  });

  const retryAbSoftSyncMutation = useMutation({
    mutationFn: (syncId) => API.payrollAutomation.retryAbSoftSync(syncId),
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.ABSOFT_HISTORY, selectedCompany]);
      message.success(t('operationSuccess', { ns: 'messages' }));
    },
    onError: (error) => {
      message.error(error.message);
    },
  });

  // Handlers
  const handleConfigChange = async (action, configId, data) => {
    switch (action) {
      case 'create':
        await createConfigMutation.mutateAsync(data);
        break;
      case 'update':
        await updateConfigMutation.mutateAsync({ configId, data });
        break;
      case 'delete':
        await deleteConfigMutation.mutateAsync(configId);
        break;
      case 'toggle':
        await toggleAutomationMutation.mutateAsync({ configId, enabled: data.enabled });
        break;
      case 'execute':
        await executeAutomationMutation.mutateAsync({ ruleType: data.ruleType, data });
        break;
      default:
        break;
    }
  };

  const handleRunWorkflow = () => {
    workflowForm.setFieldsValue({
      companyId: selectedCompany,
      month: dayjs().month() + 1,
      year: dayjs().year(),
    });
    setWorkflowModalVisible(true);
  };

  const handleWorkflowSubmit = async () => {
    try {
      const values = await workflowForm.validateFields();
      await runWorkflowMutation.mutateAsync(values);
    } catch (error) {
      console.error('Workflow submission failed:', error);
    }
  };

  const abSoftEnabled = automationConfig.some(c => c.abSoftSettings?.enabled);

  const tabItems = [
    {
      key: 'submissions',
      label: pa('submissions'),
      children: <PayrollSubmissionsPanel companyId={selectedCompany} />,
    },
    {
      key: 'companies',
      label: pa('companies'),
      children: <AutomationCompaniesPanel />,
    },
    {
      key: 'dashboard',
      label: pa('dashboard'),
      children: (
        <AutomationDashboard
          stats={dashboardStats}
          isLoading={configLoading || submissionsLoading}
        />
      ),
    },
    {
      key: 'config',
      label: pa('configuration'),
      children: (
        <AutomationConfigPanel
          companyId={selectedCompany}
          configs={automationConfig}
          onConfigChange={handleConfigChange}
          isLoading={configLoading}
        />
      ),
    },
    {
      key: 'agentConfig',
      label: pa('agentConfig.aiAgentConfiguration'),
      children: <AgentConfigPanel />,
    },
    {
      key: 'logs',
      label: pa('executionLogs'),
      children: (
        <AutomationLogsTable
          logs={logs}
          pagination={logsPagination}
          onPageChange={(_page) => {}}
          onRetry={(logId) => retryAutomationMutation.mutate(logId)}
          onViewDetails={(_logId) => {}}
          isLoading={logsLoading}
        />
      ),
    },
    {
      key: 'workflows',
      label: pa('workflows'),
      children: (
        <WorkflowExecutionView
          workflows={workflows}
          onViewDetails={(_executionId) => {}}
          onCancelWorkflow={(executionId) => cancelWorkflowMutation.mutate(executionId)}
          onDeleteWorkflow={(executionId) => deleteWorkflowMutation.mutate(executionId)}
          onRunWorkflow={handleRunWorkflow}
          isLoading={workflowsLoading}
        />
      ),
    },
    {
      key: 'absoft',
      label: pa('abSoftSync'),
      children: (
        <ABSoftSyncPanel
          companyId={selectedCompany}
          syncHistory={abSoftHistory}
          onTriggerSync={(data) => syncToAbSoftMutation.mutate(data)}
          onRetrySync={(syncId) => retryAbSoftSyncMutation.mutate(syncId)}
          onViewDetails={(_syncId) => {}}
          isLoading={abSoftLoading}
          abSoftEnabled={abSoftEnabled}
        />
      ),
    },
  ];

  // Filter tabs based on company selection and user role
  const superAdminOnlyTabs = ['config', 'agentConfig', 'logs', 'workflows', 'absoft'];
  const visibleTabs = tabItems.filter(tab => {
    // Without company selected, only show submissions and companies tabs
    if (!selectedCompany && !['submissions', 'companies'].includes(tab.key)) return false;
    // Hide superAdmin-only tabs for non-superAdmin users
    if (!isSuperAdmin && superAdminOnlyTabs.includes(tab.key)) return false;
    return true;
  });

  return (
    <RootLayout metadata={{ title: t('payrollAutomation', { ns: 'menus' }) }}>
      <ContentBackground source="/assets/images/bg-pattern.svg" />

      <Section>
        <ContainerFluid className="foreground-wrapper">
          <ContentWrapper>
            <ContentHeader title={t('payrollAutomation', { ns: 'menus' })}>
              <Select
                placeholder={pa('selectCompany')}
                className="min-w-[280px]"
                value={selectedCompany}
                onChange={setSelectedCompany}
                showSearch
                allowClear
                optionFilterProp="children"
              >
                {companies.map(company => (
                  <Option key={company._id} value={company._id}>
                    {company.name}
                  </Option>
                ))}
              </Select>
            </ContentHeader>

            <ContentBody>
              <div className="with-y-overflow">
                <div className="p-4">
                  {!selectedCompany && activeTab !== 'companies' && activeTab !== 'submissions' ? (
                    <Empty
                      description={pa('selectCompanyPrompt')}
                      className="py-16"
                    >
                      <button
                        className="btn-secondary"
                        onClick={() => setActiveTab('companies')}
                      >
                        {pa('manageCompanies')}
                      </button>
                    </Empty>
                  ) : (
                    <Tabs
                      activeKey={activeTab}
                      onChange={setActiveTab}
                      items={visibleTabs}
                      className="payroll-automation-tabs"
                    />
                  )}
                </div>
              </div>
            </ContentBody>
          </ContentWrapper>
        </ContainerFluid>
      </Section>

      {/* Workflow Modal */}
      <Modal
        title={pa('workflows')}
        open={workflowModalVisible}
        onCancel={() => setWorkflowModalVisible(false)}
        footer={
          <div className="flex justify-end gap-2">
            <button className="btn-secondary" onClick={() => setWorkflowModalVisible(false)}>
              {t('cancel', { ns: 'forms' })}
            </button>
            <button
              className="btn-primary"
              onClick={handleWorkflowSubmit}
              disabled={runWorkflowMutation.isPending}
            >
              {runWorkflowMutation.isPending ? t('loading', { ns: 'tables' }) : t('submit', { ns: 'forms' })}
            </button>
          </div>
        }
      >
        <Form form={workflowForm} layout="vertical">
          <Form.Item name="companyId" hidden>
            <input type="hidden" />
          </Form.Item>

          <Form.Item
            name="month"
            label={t('month', { ns: 'forms' })}
            rules={[{ required: true }]}
          >
            <Select placeholder={t('month', { ns: 'forms' })}>
              {[...Array(12)].map((_, i) => (
                <Option key={i + 1} value={i + 1}>
                  {dayjs().month(i).format('MMMM')}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="year"
            label={t('year', { ns: 'forms' })}
            rules={[{ required: true }]}
          >
            <InputNumber
              className="w-full"
              min={2020}
              max={2030}
            />
          </Form.Item>
        </Form>
      </Modal>
    </RootLayout>
  );
}

const PayrollAutomation = withPrivateRoute(
  PayrollAutomationPage,
  ROLES_PERMISSIONS.PAYROLL_AUTOMATION.VIEW
);

export default PayrollAutomation;
